import React, { useState, useEffect, useRef, useCallback } from 'react';
import { soaBuilderAPI } from '../../../services/api';

const FundSearchModal = ({ show, onClose, onSelectFund }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (show && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
    if (!show) {
      setQuery('');
      setResults([]);
      setSearched(false);
    }
  }, [show]);

  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    try {
      const data = await soaBuilderAPI.searchFunds(searchQuery.trim());
      setResults(data.funds || data || []);
      setSearched(true);
    } catch (err) {
      console.error('Fund search failed:', err);
      setResults([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => performSearch(value), 300);
  };

  const handleSelect = (fund) => {
    onSelectFund({
      tr_code: fund.tr_code,
      fund_name: fund.fund_name,
      asset_class: fund.asset_class,
      sub_asset_class: fund.sub_asset_class,
      blender_asset_class: fund.blender_asset_class,
      ter: fund.ter,
    });
    onClose();
  };

  if (!show) return null;

  return (
    <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
            <h5 className="modal-title">
              <i className="bi bi-search me-2"></i>Search AP List Funds
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <input
                ref={inputRef}
                type="text"
                className="form-control"
                placeholder="Search by fund name or APIR code (min 2 characters)..."
                value={query}
                onChange={handleInputChange}
              />
            </div>

            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Searching...</span>
                </div>
                <p className="text-muted mt-2 small">Searching funds...</p>
              </div>
            )}

            {!loading && searched && results.length === 0 && (
              <div className="text-center py-4">
                <i className="bi bi-inbox" style={{ fontSize: '2rem', color: 'var(--gray-color)' }}></i>
                <p className="text-muted mt-2">No results found for "{query}"</p>
              </div>
            )}

            {!loading && results.length > 0 && (
              <div className="table-responsive">
                <table className="table table-hover table-sm" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
                      <th>Fund Name</th>
                      <th>APIR Code</th>
                      <th>Asset Class</th>
                      <th>Sub Asset Class</th>
                      <th className="text-end">MER/ICR%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((fund, idx) => (
                      <tr
                        key={fund.tr_code || idx}
                        onClick={() => handleSelect(fund)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{fund.fund_name}</td>
                        <td><code>{fund.tr_code}</code></td>
                        <td>{fund.asset_class}</td>
                        <td>{fund.sub_asset_class}</td>
                        <td className="text-end">
                          {fund.ter != null ? `${(parseFloat(fund.ter)).toFixed(2)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundSearchModal;
